/**
 * WORLD module constants — dimensions, colors, ids.
 * All values in meters unless noted. Owned by WORLD.
 */

// --- Street -----------------------------------------------------------------
export const ROAD_WIDTH = 12
export const ROAD_LENGTH = 80
export const SIDEWALK_WIDTH = 4
export const SIDEWALK_HEIGHT = 0.15
/** Sidewalk centerline distance from x = 0. */
export const SIDEWALK_CENTER_X = 8

// --- Facades ----------------------------------------------------------------
export const FACADE_FRONT_X = 10
export const FACADE_DEPTH = 4
export const FACADE_CENTER_X = FACADE_FRONT_X + FACADE_DEPTH / 2
export const FACADE_MIN_HEIGHT = 9
export const END_WALL_HEIGHT = 14
export const END_WALL_SPAN = 28

// --- Alley (street -> office corridor at z ~ 0) -------------------------------
export const ALLEY_HALF_WIDTH = 2.6
export const ALLEY_WALL_HEIGHT = 4.5
export const ALLEY_X_START = 10
export const ALLEY_X_END = 30

// --- Bar / neon ---------------------------------------------------------------
export const BAR_DOOR_Z = -12
export const BAR_DOOR_WIDTH = 1.4
export const BAR_DOOR_HEIGHT = 2.6
export const NEON_SIGN_WIDTH = 6.2
export const NEON_SIGN_HEIGHT = 1.5
export const NEON_SIGN_Y = 4.4
export const NEON_SIGN_TEXT = 'THE BLUE DAHLIA'
/** Saturated tube blue — neon is never white (QA round 2). */
export const NEON_COLOR = 0x1fb6ff
export const NEON_EMISSIVE_INTENSITY = 5.0

// --- Lit background windows (depth practicals; LIGHT adds matching points) -----
export interface LitWindowDef {
  /** Center of the glow pane; x is the facade front the pane sits on. */
  readonly position: { readonly x: number; readonly y: number; readonly z: number }
  readonly width: number
  readonly height: number
  readonly color: number
  readonly emissiveIntensity: number
}
export const LIT_WINDOW_DEFS: readonly LitWindowDef[] = [
  // Far background, east side — matches LIGHT's practical at z = -28.
  { position: { x: 10, y: 6.8, z: -28 }, width: 0.9, height: 1.3, color: 0xffc473, emissiveIntensity: 1.5 },
  // Mid-distance, east side — matches LIGHT's practical at z = 8.
  { position: { x: 10, y: 5.2, z: 8 }, width: 0.85, height: 1.2, color: 0xffd9a0, emissiveIntensity: 1.3 },
  // Upper window, west side — matches LIGHT's practical at z = -16.
  { position: { x: -10, y: 7.4, z: -16 }, width: 0.9, height: 1.25, color: 0xc9d6ee, emissiveIntensity: 1.2 },
  // South-end east mass — matches LIGHT's practical at z = 24 (curtained amber).
  { position: { x: 10, y: 6.2, z: 24 }, width: 0.85, height: 1.2, color: 0xffb763, emissiveIntensity: 1.4 }
]
/** Pane sits proud of the brick so it never z-fights the facade. */
export const LIT_WINDOW_PROUD = 0.03
export const LIT_WINDOW_MULLION = 0.05
export const LIT_WINDOW_FRAME_COLOR = 0x14110d

// --- Office interior (x > 30) -------------------------------------------------
export const OFFICE_X_MIN = 30
export const OFFICE_X_MAX = 40
export const OFFICE_Z_HALF = 5
export const OFFICE_CEILING_HEIGHT = 3.2
export const OFFICE_DOOR_HALF_WIDTH = 0.65
export const OFFICE_DOOR_HEIGHT = 2.2
/** Door stands ajar so the player can walk through (no open animation event). */
export const OFFICE_DOOR_AJAR_ANGLE = -0.95
export const WINDOW_Z_HALF = 1.2
export const WINDOW_Y_MIN = 1.0
export const WINDOW_Y_MAX = 2.5
export const BLIND_SLAT_COUNT = 14
export const BLIND_SLAT_TILT = 0.85
export const DESK_HEIGHT = 0.78
export const FAN_SPEED_RAD_PER_S = 1.4

// --- Phone booth (accuse point) ------------------------------------------------
export const PHONE_BOOTH_POSITION = { x: 0, y: 0, z: -35 } as const
export const PHONE_BOOTH_ID = 'prop-phone-booth'

// --- Props ----------------------------------------------------------------------
export const LAMP_POST_HEIGHT = 4.6
export const LAMP_POST_ZS: readonly number[] = [-30, -15, 0, 15, 30]

// --- Shared palette ------------------------------------------------------------------
export const COLOR_DARK_METAL = 0x23262b
export const COLOR_HYDRANT_RED = 0x6e1f1a
export const COLOR_TRASH_CAN = 0x3a3f44
export const COLOR_BOOTH_FRAME = 0x1d2c33
export const COLOR_GLASS = 0x8aa6b5
export const COLOR_DOOR_OXBLOOD = 0x3c1714
